/**
 * Дата состояния: сегменты для набора с клавиатуры + календарь для выбора.
 *
 * Почему не `<input type="date">`. Он выглядит по-разному в каждом браузере и
 * на каждой ОС, часть из них показывает дату в НЕ русском порядке, а часть —
 * вовсе не даёт календаря. Но главное не вид: сегменты «день · месяц · год»
 * набираются с клавиатуры и озвучиваются по отдельности, а нативное поле в
 * половине сборок читается как одна строка непонятного формата.
 *
 * Локаль объявлена здесь, а не в корне приложения, ровно по одной причине:
 * корень (`main.tsx`) — не файл этой группы. Провайдер локали — контекст, он
 * дёшев, и вложенный такой же провайдер выше ничего не сломает; когда локаль
 * переедет в корень, эту обёртку нужно снять.
 *
 * Значение наружу — строка ISO `ГГГГ-ММ-ДД` либо `null`. Ни `Date`, ни
 * временной зоны: дата состояния — это КАЛЕНДАРНЫЙ день среза, и превращать
 * её в момент времени значит однажды сдвинуть её на сутки.
 */

import { CalendarDate, parseDate } from "@internationalized/date";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateSegment,
  FieldError,
  Dialog,
  Group,
  Heading,
  I18nProvider,
  Label,
  Popover,
  Text,
} from "react-aria-components";
import { defineMessages } from "../i18n/define";
import { useIntlSafe, useT } from "../i18n/hooks";
import { Explained } from "./InfoPopover";
import styles from "./controls.module.css";

const m = defineMessages({
  clear: { id: "common.kit.date.clear", defaultMessage: "Сбросить дату", description: "aria-label кнопки-крестика внутри поля даты: очистить значение" },
  fallbackLabel: { id: "common.kit.date.fallbackLabel", defaultMessage: "дата", description: "Запасное имя поля даты для экранных дикторов, если подписи нет; строчная" },
  openCalendar: { id: "common.kit.date.openCalendar", defaultMessage: "Открыть календарь", description: "aria-label кнопки с иконкой календаря в поле даты" },
  previousMonth: { id: "common.kit.date.previousMonth", defaultMessage: "Предыдущий месяц", description: "aria-label кнопки-стрелки в выпадающем календаре" },
  nextMonth: { id: "common.kit.date.nextMonth", defaultMessage: "Следующий месяц", description: "aria-label кнопки-стрелки в выпадающем календаре" },
});

/** Календарь говорит на языке интерфейса: день, месяц, год. */
function calendarLocale(locale: string): string {
  return locale === "ru" ? "ru-RU" : locale;
}

/**
 * Разбор ISO без исключения наружу. Значение приходит из URL, а URL правит
 * человек: `?as_of=вчера` не должен ронять весь экран.
 */
function parseIso(value: string | null | undefined): CalendarDate | null {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
}

export interface DateFieldProps {
  /** Видимая подпись. Без неё обязателен `aria-label`. */
  label?: ReactNode;
  "aria-label"?: string;
  /** Объяснение подписи: что именно означает эта дата. */
  explain?: ReactNode;
  /** ISO `ГГГГ-ММ-ДД` или `null` — «как знаем сейчас». */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Показывать кнопку очистки. Пустое значение — это ответ, а не пропуск. */
  isClearable?: boolean;
  /** Подпись кнопки очистки для чтения вслух. */
  clearLabel?: string;
  description?: ReactNode;
  /**
   * Значение в поле не принято (неполный год, вне диапазона). Поле
   * подсвечивается, `errorMessage` читается под ним и озвучивается.
   */
  isInvalid?: boolean;
  errorMessage?: ReactNode;
  isDisabled?: boolean;
  size?: "sm" | "md";
  className?: string;
}

export function DateField({
  label,
  "aria-label": ariaLabel,
  explain,
  value,
  onChange,
  isClearable = false,
  clearLabel,
  description,
  isInvalid = false,
  errorMessage,
  isDisabled,
  size = "md",
  className,
}: DateFieldProps) {
  const parsed = parseIso(value);
  const t = useT();
  const locale = calendarLocale(useIntlSafe().locale);

  return (
    <I18nProvider locale={locale}>
      <DatePicker
        className={`${styles.field} ${size === "sm" ? styles.sizeSm : ""} ${className ?? ""}`}
        value={parsed}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        granularity="day"
        aria-label={label ? undefined : ariaLabel}
        // `toString()` у CalendarDate — это и есть ISO календарного дня.
        onChange={(next) => onChange(next ? next.toString() : null)}
      >
        {label ? (
          <Label className={styles.label}>
            <Explained
              label={typeof label === "string" ? label : (ariaLabel ?? t(m.fallbackLabel))}
              explain={explain}
            >
              {label}
            </Explained>
          </Label>
        ) : null}

        <Group className={styles.inputShell}>
          <DateInput className={styles.dateInput}>
            {(segment) => <DateSegment segment={segment} className={styles.segment} />}
          </DateInput>
          {isClearable && parsed ? (
            <Button
              className={styles.inlineButton}
              aria-label={clearLabel ?? t(m.clear)}
              // Кнопка внутри поля не должна открывать календарь: очистка —
              // отдельное действие, а не частный случай выбора.
              slot={null}
              onPress={() => onChange(null)}
            >
              <X size={14} strokeWidth={2} aria-hidden />
            </Button>
          ) : null}
          <Button className={styles.inlineButton} aria-label={t(m.openCalendar)}>
            <CalendarIcon size={16} strokeWidth={1.75} aria-hidden />
          </Button>
        </Group>

        {description ? (
          <Text slot="description" className={styles.description}>
            {description}
          </Text>
        ) : null}

        {isInvalid && errorMessage ? (
          <FieldError className={styles.error}>{errorMessage}</FieldError>
        ) : null}

        <Popover className={styles.popoverWide}>
          <Dialog>
            <Calendar className={styles.calendar}>
              <header className={styles.calendarHeader}>
                <Button className={styles.inlineButton} slot="previous" aria-label={t(m.previousMonth)}>
                  <ChevronLeft size={16} strokeWidth={1.75} aria-hidden />
                </Button>
                <Heading className={styles.calendarHeading} />
                <Button className={styles.inlineButton} slot="next" aria-label={t(m.nextMonth)}>
                  <ChevronRight size={16} strokeWidth={1.75} aria-hidden />
                </Button>
              </header>
              <CalendarGrid className={styles.calendarGrid}>
                <CalendarGridHeader>
                  {(day) => (
                    <CalendarHeaderCell className={styles.calendarWeekday}>
                      {day}
                    </CalendarHeaderCell>
                  )}
                </CalendarGridHeader>
                <CalendarGridBody>
                  {(date) => <CalendarCell date={date} className={styles.calendarCell} />}
                </CalendarGridBody>
              </CalendarGrid>
            </Calendar>
          </Dialog>
        </Popover>
      </DatePicker>
    </I18nProvider>
  );
}
